// Kits (SPEC F4, §8 Kit and PadSettings): 16 pads, A01-A16, each with a
// sample, level, pan, tune, decay, low-pass, choke group and role. Saved,
// renamed, duplicated and linked to a project. Delete is soft, with undo
// (DESIGN_SYSTEM §5). This is the data layer only; the Kit screen is later.
//
// The `kits` table has had every index this needs since schema v1
// (`&id, projectId, styleId, updatedAt, deletedAt`), so there is no version
// bump (DECISIONS).

import type { Kit, PadSettings, Project, StyleId } from '../../data/types';
import { getDb } from '../schema';
import { UNDO_WINDOW_MS } from './marks';

export { UNDO_WINDOW_MS };

/** A01 to A16 (SPEC F4). */
export const PAD_COUNT = 16;

const newId = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/** An empty pad: unity level, centred, untuned, full decay (DECISIONS 9). */
export function defaultPad(): PadSettings {
  return { level: 1, pan: 0, tuneSemi: 0, tuneCents: 0, decay: 1 };
}

/**
 * One pad with every number in range: level and decay 0..1, pan -1..1, tune within 12 semitones and
 * 100 cents, low-pass 20..20000 Hz, choke an integer group 1..16. A missing or non-numeric value
 * takes the default; an optional field that is absent stays absent. A preferred perc kind stays only on a perc pad.
 */
export function clampPad(pad: Partial<PadSettings> = {}): PadSettings {
  const out: PadSettings = {
    level: clamp(num(pad.level, 1), 0, 1),
    pan: clamp(num(pad.pan, 0), -1, 1),
    tuneSemi: clamp(Math.round(num(pad.tuneSemi, 0)), -12, 12),
    tuneCents: clamp(Math.round(num(pad.tuneCents, 0)), -100, 100),
    decay: clamp(num(pad.decay, 1), 0, 1),
  };
  if (typeof pad.sampleId === 'string' && pad.sampleId) out.sampleId = pad.sampleId;
  if (pad.role) out.role = pad.role;
  // v0.5.1: only a perc pad prefers a kind
  if (pad.role === 'perc' && pad.percKind) out.percKind = pad.percKind;
  if (typeof pad.lpfHz === 'number' && Number.isFinite(pad.lpfHz)) out.lpfHz = clamp(pad.lpfHz, 20, 20000);
  if (typeof pad.choke === 'number' && Number.isFinite(pad.choke)) out.choke = clamp(Math.round(pad.choke), 1, PAD_COUNT);
  return out;
}

/** Exactly 16 pads: extra ones dropped, missing ones empty, every one clamped. */
export function normalizePads(pads: readonly Partial<PadSettings>[] = []): PadSettings[] {
  return Array.from({ length: PAD_COUNT }, (_, i) => clampPad(pads[i]));
}

export interface NewKit {
  name: string;
  styleId?: StyleId;
  projectId?: string;
  pads?: readonly Partial<PadSettings>[];
}

function cleanName(name: string): string {
  const n = name.trim();
  if (!n) throw new RangeError('A kit needs a name');
  return n;
}

export async function createKit(input: NewKit, now: number): Promise<Kit> {
  const kit: Kit = { id: newId(), name: cleanName(input.name), pads: normalizePads(input.pads), updatedAt: now };
  if (input.styleId) kit.styleId = input.styleId;
  if (input.projectId) kit.projectId = input.projectId;
  await getDb().kits.put(kit);
  return kit;
}

/** A live (not deleted) kit. */
export async function getKit(id: string): Promise<Kit | undefined> {
  const kit = await getDb().kits.get(id);
  return kit && kit.deletedAt === undefined ? kit : undefined;
}

/** Live kits, most recently changed first, optionally for one style or project. */
export async function listKits(filter: { styleId?: StyleId; projectId?: string } = {}): Promise<Kit[]> {
  const all = await getDb().kits.orderBy('updatedAt').reverse().toArray();
  return all.filter((k) => k.deletedAt === undefined && (!filter.styleId || k.styleId === filter.styleId) && (!filter.projectId || k.projectId === filter.projectId));
}

export async function savePads(id: string, pads: readonly Partial<PadSettings>[], now: number): Promise<void> {
  await getDb().kits.update(id, { pads: normalizePads(pads), updatedAt: now });
}

export async function renameKit(id: string, name: string, now: number): Promise<void> {
  await getDb().kits.update(id, { name: cleanName(name), updatedAt: now });
}

/** A copy named "<name> copy", with its own pads. It keeps the style but is not linked to the project the original is (a project has one kit). */
export async function duplicateKit(id: string, now: number): Promise<Kit | undefined> {
  const db = getDb();
  return db.transaction('rw', db.kits, async () => {
    const source = await db.kits.get(id);
    if (!source || source.deletedAt !== undefined) return undefined;
    const copy: Kit = { id: newId(), name: `${source.name} copy`, pads: normalizePads(source.pads), updatedAt: now };
    if (source.styleId) copy.styleId = source.styleId;
    await db.kits.put(copy);
    return copy;
  });
}

export async function softDeleteKit(id: string, now: number): Promise<void> {
  await getDb().kits.update(id, { deletedAt: now });
}

export async function restoreKit(id: string): Promise<void> {
  await getDb().kits.update(id, { deletedAt: undefined });
}

/** Deleted kits past the undo window are gone for good, and no project keeps pointing at them. */
export async function purgeExpiredKits(now: number): Promise<number> {
  const db = getDb();
  return db.transaction('rw', db.kits, db.projects, async () => {
    const dead = (await db.kits.toArray()).filter((k) => k.deletedAt !== undefined && now - k.deletedAt > UNDO_WINDOW_MS);
    if (dead.length === 0) return 0;
    const ids = new Set(dead.map((k) => k.id));
    for (const p of await db.projects.toArray()) if (p.kitId && ids.has(p.kitId)) await db.projects.update(p.id, { kitId: undefined });
    await db.kits.bulkDelete([...ids]);
    return dead.length;
  });
}

/**
 * Makes `kitId` the project's kit, in one transaction (a failure changes nothing). A project has one
 * kit and a kit belongs to one project, so the project's previous kit and the kit's previous project
 * are unlinked. Both must exist and not be deleted.
 */
export async function linkKitToProject(kitId: string, projectId: string, now: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.kits, db.projects, async () => {
    const kit = await db.kits.get(kitId);
    const project = await db.projects.get(projectId);
    if (!kit || kit.deletedAt !== undefined) throw new Error(`No kit ${kitId}`);
    if (!project || project.deletedAt !== undefined) throw new Error(`No project ${projectId}`);

    if (project.kitId && project.kitId !== kitId) await db.kits.update(project.kitId, { projectId: undefined, updatedAt: now });
    if (kit.projectId && kit.projectId !== projectId) await db.projects.update(kit.projectId, { kitId: undefined, updatedAt: now });
    await db.kits.update(kitId, { projectId, updatedAt: now });
    await db.projects.update(projectId, { kitId, updatedAt: now });
  });
}

/** Unlinks a kit from its project, both sides, in one transaction. */
export async function unlinkKit(kitId: string, now: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.kits, db.projects, async () => {
    const kit = await db.kits.get(kitId);
    if (!kit?.projectId) return;
    const project: Project | undefined = await db.projects.get(kit.projectId);
    await db.kits.update(kitId, { projectId: undefined, updatedAt: now });
    if (project?.kitId === kitId) await db.projects.update(project.id, { kitId: undefined, updatedAt: now });
  });
}
